import { WebsocketService } from './services/websocket.service';
import { Router } from '@angular/router';
import { AuthService, ToastService } from 'app/services';
import { Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest, HttpEvent } from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { isPlatformServer } from '@angular/common';
import { UtilityService } from "./services";
import { MessageType, BypassAPIGlobalHandleEnums } from './constants';
import { TranslateService } from '@ngx-translate/core';
import { PwaService } from './services/pwa.service';

@Injectable({
  providedIn: 'root'
})

export class AppHttpInterceptor implements HttpInterceptor {

  constructor(
    private authService: AuthService,
    private utilityService: UtilityService,
    private router: Router,
    private websocketService: WebsocketService,
    private toastService: ToastService,
    private translationService: TranslateService,
    // Looked up lazily: PwaService -> AuthService -> HttpClient -> this interceptor would be a DI cycle.
    private injector: Injector,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  intercept(originalRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Append the session ID header, unless we are getting the translation data that creates a CORS problem on local envs
    const request = originalRequest.url.includes('translation/data') || originalRequest.url.includes('google') || originalRequest.url.includes('strapi') ?
      originalRequest :
      originalRequest.clone({ headers: originalRequest.headers.append("X-Session-ID", this.utilityService.sessionId) });

    return next.handle(request).pipe(
      catchError((response: HttpErrorResponse) => {
        // Don't use interceptor on server
        if (isPlatformServer(this.platformId)) {
          return throwError(response);
        }

        if (response.status === 0) {
          // No response at all: a network-level failure. The browser fires no `offline` event when
          // Wi-Fi is up but the internet is not, so let PwaService verify against the server and
          // show the offline dialog if that probe fails too. The error itself is still rethrown below.
          this.injector.get(PwaService).syncConnectivity();
        }

        if (response.status === 403) {
          // Forward 403s for the login flow and for chat file requests so the
          // caller can handle them locally (e.g. show "file not accessible")
          // instead of being redirected to the global 403 page. All other 403s
          // keep redirecting as before.
          if (request.url.includes(`/auth/login`) || request.url.includes(`message/chat-file`)) {
            return throwError(response);
          }
          this.router.navigate(['/403']);
          return EMPTY;
        }

        // Forward all non-401 errors
        if (response.status !== 401) {
          const error = response.error.error;
          if (!error) {
            return throwError(response);
          }
          if (error.reason_keys && request.headers.get(BypassAPIGlobalHandleEnums.ToastMessageHandle) !== 'true') {
            error.reason_keys.forEach(key => {
              this.toastService.showMsg('texts.' + key, MessageType.error);
            });
          }
          if (request.headers.get(BypassAPIGlobalHandleEnums.ErrorHandle) === 'true') {
            return throwError(response);
          }
          return EMPTY;
        }

        // Forward 401 error on refresh or login URL
        if (request.url.includes(`/auth/refresh`) || request.url.includes(`/auth/login`)) {
          return throwError(response);
        }

        // Retry all other 401 errors by refreshing the token first, then repeating the original request
        return this.authService.refresh().pipe(
          switchMap((token) => {
            if (token) {
              return next.handle(request.clone({
                setHeaders: {
                  Authorization: `Bearer ${token}`,
                },
              }));
            } else {
              const wasAuthenticated = !!this.authService.user;
              this.authService.clearSession();
              if (wasAuthenticated) {
                this.translationService.get('links.Login_page').subscribe(link => {
                  this.router.navigate([link]);
                });
              }
              return throwError(response);
            }
          }),
          catchError(error => {
            if ((error.headers?.status === 401 || error.text_key === "Misc_NotAuthenticated") && this.authService.user) {
              this.router.navigate(['/401']);
              this.websocketService.close();
            }
            return throwError(error);
          })
        );
      })
    );
  }
}